import Delivery from '@/modules/Logistics/api/models/Delivery';

/**
 * DeliveryRepository - VuexORM queries for delivery lists
 */
export default class DeliveryRepository {
    /**
     * Get all deliveries
     */
    public static getAll(): Delivery[] {
        return Delivery.query()
            .withAllRecursive()
            .orderBy((delivery) => new Date((delivery as Delivery).date).getTime(), 'desc')
            .get();
    }

    /**
     * Get all deliveries sorted by date (newest first)
     */
    public static getAllSortedByDate(): Delivery[] {
        return Delivery.query().withAllRecursive().orderBy('date', 'desc').get();
    }

    /**
     * Get a single delivery by ID
     */
    public static getById(id: string): Delivery | null {
        if (!id) {
            return null;
        }
        return Delivery.query().whereId(id).withAllRecursive().first();
    }

    /**
     * Get deliveries by date
     */
    public static getByDate(date: string): Delivery[] {
        return Delivery.query().where('date', date).withAllRecursive().get();
    }

    /**
     * Get deliveries by driver
     */
    public static getByDriver(driver: string): Delivery[] {
        return Delivery.query().where('driver', driver).withAllRecursive().get();
    }

    /**
     * Get deliveries by vehicle
     */
    public static getByVehicle(vehicle: string): Delivery[] {
        return Delivery.query().where('vehicle', vehicle).withAllRecursive().get();
    }

    /**
     * Get unprinted deliveries
     */
    public static getUnprinted(): Delivery[] {
        return Delivery.query().where('isPrinted', false).withAllRecursive().get();
    }

    /**
     * Get printed deliveries
     */
    public static getPrinted(): Delivery[] {
        return Delivery.query().where('isPrinted', true).withAllRecursive().get();
    }

    /**
     * Search deliveries by query string
     * Searches across driver, vehicle, number fields
     */
    public static search(query: string): Delivery[] {
        if (!query) {
            return DeliveryRepository.getAll();
        }

        const lowerQuery = query.toLowerCase();

        return Delivery.query()
            .where((delivery: Delivery) => {
                return (
                    delivery.driver.toLowerCase().includes(lowerQuery) ||
                    delivery.vehicle.toLowerCase().includes(lowerQuery) ||
                    delivery.number.toLowerCase().includes(lowerQuery)
                );
            })
            .withAllRecursive()
            .get();
    }

    /**
     * Get deliveries count
    /**
     * Get deliveries count
     */
    public static getCount(): number {
        return Delivery.query().count();
    }

    /**
     * Check if delivery exists by ID
     */
    public static exists(id: string): boolean {
        return Delivery.query().whereId(id).first() !== null;
    }

    /**
     * Delete delivery from local store
     */
    public static deleteLocal(id: string): void {
        Delivery.delete(id);
    }

    /**
     * Delete multiple deliveries from local store
     */
    public static deleteMultipleLocal(ids: string[]): void {
        for (const id of ids) {
            Delivery.delete(id);
        }
    }

    /**
     * Clear all deliveries from local store
     */
    public static clearAll(): void {
        Delivery.deleteAll();
    }
}
